import React from 'react';
import {
  Alert,
  Button,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import SummaryCardsRow from './SummaryCardsRow.jsx';
import VarianceCardList from './VarianceCardList.jsx';
import VarianceSummaryControls from './VarianceSummaryControls.jsx';
import VarianceTable from './VarianceTable.jsx';
import { billingErrorMessage } from './errorMessage.js';

const VarianceSummaryPanel = ({
  drawerOpen,
  exportDisabled,
  filtered,
  isCompact,
  noneSelected,
  onExport,
  onRowClick,
  onSearchChange,
  onRefreshAll,
  onRefreshReasons,
  reconciled,
  search,
  selectedBac,
  summary,
  modifiedTotals,
  contextWarmStatus,
  tableView,
  threshold,
  varianceError,
  varianceLoading,
  varianceListMeta,
  onRetryVariances,
  fileHealthByBac,
  selectedVarianceIds,
  allVisibleSelected,
  someVisibleSelected,
  onToggleVarianceSelection,
  onToggleAllVisibleVariances,
  onResetVarianceReviews,
  resetVarianceReviewsDisabled,
  resetVarianceReviewsBusy,
  selectedVarianceCount,
}) => (
  <Stack spacing={2}>
    <SummaryCardsRow
      summary={summary}
      threshold={threshold}
      modifiedTotals={modifiedTotals}
      contextWarmStatus={contextWarmStatus}
    />

    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 1.5 }}>
      <Stack spacing={2}>
        <VarianceSummaryControls
          savedView={tableView.savedView}
          status={tableView.status}
          driver={tableView.driver}
          enrollment={tableView.enrollment}
          groupBy={tableView.groupBy}
          options={tableView.options}
          shownCount={filtered.length}
          totalCount={reconciled.length}
          search={search}
          exportDisabled={exportDisabled}
          onSavedViewChange={tableView.onSavedViewChange}
          onStatusChange={tableView.onStatusChange}
          onDriverChange={tableView.onDriverChange}
          onEnrollmentChange={tableView.onEnrollmentChange}
          onGroupByChange={tableView.onGroupByChange}
          onSearchChange={onSearchChange}
          onExport={() => onExport(filtered)}
          onRefreshAll={onRefreshAll}
          onRefreshReasons={onRefreshReasons}
          onReset={tableView.onReset}
          onResetVarianceReviews={onResetVarianceReviews}
          resetVarianceReviewsDisabled={resetVarianceReviewsDisabled}
          resetVarianceReviewsBusy={resetVarianceReviewsBusy}
          selectedVarianceCount={selectedVarianceCount}
        />
        {varianceLoading ? (
          <Stack spacing={1}>
            <Skeleton height={42} />
            <Skeleton height={42} />
            <Skeleton height={42} />
          </Stack>
        ) : varianceError ? (
          <Alert
            severity="error"
            action={onRetryVariances ? <Button color="inherit" size="small" onClick={onRetryVariances}>Retry</Button> : null}
          >
            {billingErrorMessage(varianceError, 'Variance rows could not be loaded.')}
          </Alert>
        ) : (
          <>
            {varianceListMeta?.hasMore ? (
              <Alert
                severity="warning"
                action={onRetryVariances ? <Button color="inherit" size="small" onClick={onRetryVariances}>Retry</Button> : null}
              >
                Variance rows are partially loaded ({Number(varianceListMeta.returned || reconciled.length).toLocaleString()}
                {' '}of {Number(varianceListMeta.total || 0).toLocaleString()}). Refresh before using this queue for final review.
              </Alert>
            ) : null}
            {filtered.length === 0 ? (
              <Alert severity="success" sx={{ my: 2 }}>
                {noneSelected
                  ? 'No categories selected. Click a filter chip above to show rows.'
                  : 'No rows match the current filters. Clean month!'}
              </Alert>
            ) : isCompact ? (
              <VarianceCardList rows={filtered} onRowClick={onRowClick} fileHealthByBac={fileHealthByBac} />
            ) : (
              <VarianceTable
                rows={filtered}
                selectedBac={selectedBac}
                drawerOpen={drawerOpen}
                onRowClick={onRowClick}
                fileHealthByBac={fileHealthByBac}
                groupBy={tableView.groupBy}
                sortBy={tableView.sortBy}
                sortDirection={tableView.sortDirection}
                onSortChange={tableView.onSortChange}
                selectedVarianceIds={selectedVarianceIds}
                allVisibleSelected={allVisibleSelected}
                someVisibleSelected={someVisibleSelected}
                onToggleVarianceSelection={onToggleVarianceSelection}
                onToggleAllVisibleVariances={onToggleAllVisibleVariances}
              />
            )}
          </>
        )}

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Visible variance rows: {filtered.length} of {reconciled.length}. Click any row to open the 3-way compare.
        </Typography>
      </Stack>
    </Paper>
  </Stack>
);

export default VarianceSummaryPanel;
